import { Injectable, NgZone } from '@angular/core';
import RecordRTC from 'recordrtc';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { Duration } from 'moment';
import { WebAssemblyRecorder, MediaStreamRecorder } from 'recordrtc';

interface RecordedVideoOutput {
  blob: Blob;
  url: string;
  title: string;
}

@Injectable({
  providedIn: 'root'
})
export class VideoRecordingService {

  private stream;
  private recorder;
  private interval;
  private startTime;
  private privateStream = new Subject<MediaStream>();
  private privateRecorded = new Subject<RecordedVideoOutput>();
  private privateRecordedUrl = new Subject<string>();
  private privateRecordedTime = new Subject<Duration>();
  private privateRecordingFailed = new Subject<string>();


  getRecordedUrl(): Observable<string> {
    return this.privateRecordedUrl.asObservable();
  }

  getRecordedBlob(): Observable<RecordedVideoOutput> {
    return this.privateRecorded.asObservable();
  }

  getRecordedTime(): Observable<Duration> {
    return this.privateRecordedTime.asObservable();
  }

  recordingFailed(): Observable<string> {
    return this.privateRecordingFailed.asObservable();
  }

  getStream(): Observable<MediaStream> {
    return this.privateStream.asObservable();
  }

  showCamera(conf: any): Promise<any> {
    const browser = navigator as any;
    if (this.recorder) {
      // It means recording is already started or it is already recording something
      return;
    }

    return new Promise((resolve, reject) => {
      browser.mediaDevices.getUserMedia(conf).then(stream => {
        this.stream = stream;
        resolve(this.stream);
      }).catch(error => {
        this.privateRecordingFailed.next();
      });
    });
  }

  startRecording(conf: any): Promise<any> {
    const browser = navigator as any;
    if (this.recorder) {
      // It means recording is already started or it is already recording something
      return;
    }

    const currentTime = moment();
    const diffTime = moment.duration(currentTime.diff(currentTime));
    this.privateRecordedTime.next(diffTime);
    return new Promise((resolve, reject) => {
      browser.mediaDevices.getUserMedia(conf).then(stream => {
        this.stream = stream;
        this.record();
        resolve(this.stream);
      }).catch(error => {
        this.privateRecordingFailed.next();
      });
    });
  }

  abortRecording() {
    this.stopMedia();
  }

  isApple() {
    return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ].includes(navigator.platform)
      || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
  }

  private record() {
    if (this.isApple()) {
      console.log('Apple product detected.');
    }
    const mimeType = this.isApple() ? 'video/webm' : 'video/webm';

    this.recorder = new RecordRTC(this.stream, {
      recorderType: MediaStreamRecorder,
      mimeType: mimeType,
      type: 'video',
      frameInterval: 24,
      frameRate: 24,
      bitsPerSecond: 2000000,
      canvas: {
        width: 1280,
        height: 720
      },
    });
    this.recorder.startRecording();
    this.startTime = moment();
    this.interval = setInterval(
      () => {
        const currentTime = moment();
        const diffTime = moment.duration(currentTime.diff(this.startTime));
        this.privateRecordedTime.next(diffTime);
        this.privateStream.next(this.stream);
      },
      500
    );
  }

  stopRecording() {
    if (this.recorder) {
      this.recorder.stopRecording(this.processVideo.bind(this));
    }
    this.stopStreams();
  }

  stopStreams() {
    if (this.stream) {
      this.stream.getAudioTracks().forEach(track => track.stop());
      this.stream.getVideoTracks().forEach(track => track.stop());
      this.stream.stop();
      this.stream = null;
    }
  }

  private processVideo(audioVideoWebMURL) {
    const recordedBlob = this.recorder.getBlob();
    this.recorder.getDataURL(function (dataURL) { });
    const recordedName = encodeURIComponent('video_' + new Date().getTime() + '.mp4');
    this.privateRecorded.next({ blob: recordedBlob, url: audioVideoWebMURL, title: recordedName });
    this.stopMedia();
  }

  private stopMedia() {
    if (this.recorder) {
      this.recorder = null;
      clearInterval(this.interval);
      this.startTime = null;
      this.stopStreams();
    }
  }
}

